import * as React from 'react'
import * as z from 'zod'

import { BranchSwitcher as BranchSwitcherPrimitive } from '~/sotano-kit/primitives/branch-switcher'
import { Skeleton } from '~/sotano-kit/primitives/skeleton'
import {
  useBranchOrCommitSegment,
  useSelectedBranch,
  useSelectedBranchOrCommitQuery,
} from '~/lib/commits/use-selected-branch-or-commit'
import { getId } from '@basehub/utils/id/index'
import {
  Repository,
  Team,
  isCommit,
  isPlayground,
} from '@basehub/data/replicache/index'
import { useCommitBlockTreeManager } from '~/hooks-data/blocks-tree'
import { useUserStore } from '~/app/_store/user-store'
import { useBranches } from '~/hooks-data/use-branches'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import { useSuggestedBranches } from '~/hooks-data/use-suggested-branches'
import { useTabsStore } from '../(repo)/[repo-slug]/(editor)/explore/components/tabs/store'
import { GitIcon } from '~/components/icons/git'
import { clsx } from 'clsx'
import { Button, ButtonLink } from '~/sotano-kit/primitives/button'
import { ArchiveIcon } from '~/components/icons/archive'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useIsGuestInOthersBranch } from '~/hooks-data/use-user-role'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/sotano-kit/primitives/dialog'
import { BranchMergeIcon, MergeIcon } from '~/components/icons/branch-icon'
import {
  CubeIcon,
  OpenInNewWindowIcon,
  Pencil1Icon,
} from '@radix-ui/react-icons'
import { LabeledInput } from '~/sotano-kit/primitives/input'
import { useDialogSteps } from '~/hooks/use-dialog-steps'
import { toast } from 'sonner'
import { useStorage } from '~/hooks/use-storage'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import {
  PublicRepo,
  PublicTeam,
  isForkableRepo,
} from '@basehub/data/replicache/mutators/_public-types'
import { useSelectedTeamAndRepo } from '~/hooks-data/use-selected-team-and-repo'
import { getLoginHref } from '../../(login)/login/get-login-href'
import { usePathname, useRouter } from 'next/navigation'
import { encodeNextParam } from '~/app/(mkt)/_index-redirector/server'
import { useToggleState } from '~/hooks/use-toggle-state'
import { useCreateBranchMutator } from '~/replicache/frontend/use-create-branch-mutator'
import { ForkDialog } from './fork-dialog'
import { getPlaygroundExpirationDate } from '@basehub/data/replicache/playground/utils'
import { useTimeDiff } from '~/hooks/use-time-diff'
import { usePlaygroundCookie } from '~/lib/playground/use-playground-id'

export const BranchSwitcher = () => {
  const addTab = useTabsStore((s) => s.addTab)
  const replicache = useRepoReplicache()?.data?.replicache
  const userId = useUserStore((s) => s.optimisticUser?.id)
  const branchesQuery = useBranches({ status: 'open', archived: false })
  const selectedBranchOrCommitQuery = useSelectedBranchOrCommitQuery()
  const playgroundCookie = usePlaygroundCookie()
  const suggestedBranches = useSuggestedBranches().data
  const { branchOrCommit, repoSlug, repoPage, teamSlug } = useDashboardSlugs()
  const isLoggedIn = useUserStore((s) => s.optimisticUser?.id)
  const [lastVisitedBranches, setLastVisitedBranches] = useLastVisitedBranch()
  const data = useSelectedTeamAndRepo().data
  const team = data?.team
  const repo = data?.repo
  const headCommit = selectedBranchOrCommitQuery.data
    ? isCommit(selectedBranchOrCommitQuery.data)
      ? selectedBranchOrCommitQuery.data
      : (selectedBranchOrCommitQuery.data.headCommit ?? null)
    : undefined
  const repository = data?.repo

  const branch = React.useMemo(() => {
    if (!selectedBranchOrCommitQuery.data) return null
    if (isCommit(selectedBranchOrCommitQuery.data)) {
      const branchId = selectedBranchOrCommitQuery.data.branchId
      return branchesQuery.data?.find((b) => b.id === branchId)
    }
    return selectedBranchOrCommitQuery.data
  }, [branchesQuery.data, selectedBranchOrCommitQuery.data])
  const isPlayground =
    branch &&
    ((branch.isDefault && !!playgroundCookie) || !!branch?.playgroundId)

  const { data: headCommitBlockTree } = useCommitBlockTreeManager({
    commitId: selectedBranchOrCommitQuery.data
      ? isCommit(selectedBranchOrCommitQuery.data)
        ? selectedBranchOrCommitQuery.data.id
        : (selectedBranchOrCommitQuery.data.headCommitId ?? undefined)
      : undefined,
    rootBlockId: selectedBranchOrCommitQuery.data
      ? isCommit(selectedBranchOrCommitQuery.data)
        ? (selectedBranchOrCommitQuery.data.rootBlockId ?? undefined)
        : (selectedBranchOrCommitQuery.data.headCommit?.rootBlockId ??
          undefined)
      : undefined,
    caller: 'branch switcher',
  })

  const canCreateNewBranch = headCommit
    ? !!headCommitBlockTree
    : headCommit === null

  const createBranch = useCreateBranchMutator()
  const isGuestInOthersBranch = useIsGuestInOthersBranch()

  React.useEffect(() => {
    if (!repoSlug || !branchOrCommit) return
    const lastVisitedBranch = lastVisitedBranches[repoSlug] ?? ''
    if (lastVisitedBranch === branchOrCommit) return
    setLastVisitedBranches({
      ...lastVisitedBranches,
      [repoSlug]: branchOrCommit,
    })
  }, [branchOrCommit, lastVisitedBranches, repoSlug, setLastVisitedBranches])

  const showSuggestedBranchesBadge = Boolean(
    suggestedBranches?.length &&
      selectedBranchOrCommitQuery.data &&
      !isCommit(selectedBranchOrCommitQuery.data) &&
      !selectedBranchOrCommitQuery.data.git?.branch
  )

  const isLoadingBranchSwitcher = Boolean(
    branchesQuery.isLoading ||
      selectedBranchOrCommitQuery.isLoading ||
      !selectedBranchOrCommitQuery.data
  )

  const activeBranchOrCommit = selectedBranchOrCommitQuery.data

  return (
    <div
      className={clsx(
        'flex min-h-[26px] justify-between pl-2 gap-x-2',
        showSuggestedBranchesBadge && 'pr-2'
      )}
    >
      <div className="flex items-center text-sm">
        {!isLoadingBranchSwitcher ? (
          <BranchSwitcherPrimitive
            activeBranchOrCommit={activeBranchOrCommit!}
            branches={branchesQuery.data ?? []}
            disableOnCreate={!isLoggedIn || !canCreateNewBranch}
            header={false}
            onBranchCreate={
              isLoggedIn
                ? async ({ name }) => {
                    if (!canCreateNewBranch) return
                    if (!selectedBranchOrCommitQuery.data || !userId) {
                      return
                    }
                    await createBranch({
                      checkout: true,
                      toastSuccess: true,
                      toastError: true,
                      data: {
                        authorId: userId,
                        name,
                        createdAt: new Date().toISOString(),
                        id: getId(),
                        isDefault: false,
                      },
                      baseBranchOrCommitId: selectedBranchOrCommitQuery.data.id,
                      idSeed: getId(),
                    })
                  }
                : undefined
            }
          />
        ) : (
          <Skeleton height={20} width={80} />
        )}
      </div>

      {selectedBranchOrCommitQuery.data &&
        isCommit(selectedBranchOrCommitQuery.data) &&
        branch && (
          <ButtonLink
            href={`/${teamSlug}/${repoSlug}/${repoPage}/${branch.name}`}
            unstyled
            focusStyled
            className="bg-dark-control-base/15 rounded-md text-xs leading-[26px] font-medium px-2.5 text-dark-control-base cursor-default hover:bg-dark-control-base/30"
          >
            Go back to {branch.name}
          </ButtonLink>
        )}

      {Boolean(
        !isLoadingBranchSwitcher &&
          !isPlayground &&
          (isGuestInOthersBranch || (isForkableRepo(repo) && !isLoggedIn))
      ) && <ContributeButton />}

      {isPlayground && !!team && !!repository && (
        <ClaimPlaygroundButton team={team} repository={repository} />
      )}

      {showSuggestedBranchesBadge && (
        <IconButton
          className="bg-dark-accent/15 hover:!bg-dark-accent/30 text-dark-accent text-xs"
          onClick={() => {
            addTab({
              key: 'branches:suggested',
              partiallyOpen: false,
            })
          }}
        >
          {suggestedBranches?.length}&nbsp;
          <GitIcon className="size-[15px]" />
        </IconButton>
      )}

      {Boolean(
        selectedBranchOrCommitQuery?.data &&
          !isCommit(selectedBranchOrCommitQuery?.data) &&
          selectedBranchOrCommitQuery?.data?.archivedAt
      ) && (
        <div className="h-[26px] font-medium flex">
          <div className="bg-dark-shade border border-r-0 pl-2 pr-1.5 text-dark-muted border-dark-border-solid rounded-l-md flex items-center">
            <ArchiveIcon className="mr-1.5" />
            This branch is archived.
          </div>
          <hr className="h-full w-px bg-dark-border-solid border-none" />
          <Button
            unstyled
            focusStyled
            className="rounded-r-md px-2.5 border-l-0 bg-dark-bg hover:bg-dark-shade rounded-l-none border border-dark-border-solid cursor-default"
            aria-label="Restore archived branch"
            onClick={async () => {
              if (!replicache || !selectedBranchOrCommitQuery.data) return

              await replicache.mutate.toggleArchiveBranch({
                id: selectedBranchOrCommitQuery.data.id,
                archivedAt: new Date().toISOString(),
                archivedBy: '',
                archivedByUsername: '',
              })
            }}
          >
            Restore
          </Button>
        </div>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------------------------------
 * Claim playground dialog
 * -----------------------------------------------------------------------------------------------*/

const ClaimPlaygroundButton = ({
  team,
  repository,
}: {
  team: Team | PublicTeam
  repository: Repository | PublicRepo
}) => {
  const forkToggleState = useToggleState()
  const branch = useSelectedBranch().branch
  const playgroundCookie = usePlaygroundCookie()
  const [isInIframe, setIsInIframe] = React.useState<boolean>(false)
  const { teamSlug, repoSlug, repoPage } = useDashboardSlugs()
  const { encodedSegment } = useBranchOrCommitSegment()
  // eslint-disable-next-line react-compiler/react-compiler
  const timeToExpiration = useTimeDiff(
    branch && isPlayground(branch)
      ? getPlaygroundExpirationDate(branch)
      : new Date(),
    {
      refreshIntervalMs: 1000 * 60 * 10, // refresh every 10 minutes
      short: false,
    }
  )

  React.useEffect(() => {
    setIsInIframe(window.self !== window.top)
  }, [])

  if (
    !branch ||
    (!branch.playgroundId && !branch?.isDefault && playgroundCookie)
  ) {
    return null
  }

  return (
    <>
      <div className="rounded-md bg-dark-control-base/15 pl-1 border border-dark-control-base/15 text-dark-control-base text-xs flex font-medium">
        {
          <span className="pr-1.5 flex items-center whitespace-nowrap gap-x-1">
            <CubeIcon />
            {Boolean(branch.playgroundExpiresAt)
              ? `Playground expires ${timeToExpiration}`
              : 'This is a playground'}
          </span>
        }
        <Button
          onClick={forkToggleState.handleToggle}
          unstyled
          focusStyled
          className="border-l rounded-l-none rounded-r-md -mr-px bg-dark-control-base/15 -my-px border-dark-control-base/15 hover:bg-dark-control-base/30 px-1.5 cursor-default"
        >
          Claim it
        </Button>
        <ForkDialog
          toggleState={forkToggleState}
          team={team}
          repository={repository}
          queryParam="claim"
          hideTrigger
        />
      </div>

      {isInIframe && (
        <ButtonLink
          unstyled
          target="_blank"
          href={`/${teamSlug}/${repoSlug}/${repoPage}/${encodedSegment}`}
          className="text-xs flex items-center gap-x-0.5 hover:underline text-dark-muted"
        >
          <span>Open in new tab</span>
          <OpenInNewWindowIcon />
        </ButtonLink>
      )}
    </>
  )
}

/* -------------------------------------------------------------------------------------------------
 * Contribute dialog
 * -----------------------------------------------------------------------------------------------*/

const ContributeButton = () => {
  const selectedBranchOrCommitQuery = useSelectedBranchOrCommitQuery()
  const createBranch = useCreateBranchMutator()
  const userId = useUserStore((s) => s.optimisticUser?.id)
  const pathname = usePathname()
  const router = useRouter()
  const { wrapperRef, setActiveStepIndex, initialStylesForInactiveSteps } =
    useDialogSteps()
  const toggleState = useToggleState()

  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search)

    if (searchParams.has('contribute')) {
      if (userId) {
        toggleState.handleOn()
      } else {
        router.replace(
          getLoginHref(
            `/login?next=${encodeNextParam(pathname + '?contribute')}`
          )
        )
      }
    }
  }, [pathname, router, toggleState, userId])

  return (
    <Dialog
      open={toggleState.isOn}
      onOpenChange={(open) => {
        setActiveStepIndex(0)
        if (open) toggleState.handleOn()
        else {
          const searchParams = new URLSearchParams(window.location.search)
          searchParams.delete('contribute')
          window.history.replaceState(
            {},
            '',
            `${window.location.pathname}?${searchParams}`
          )
          toggleState.handleOff()
        }
      }}
    >
      {userId ? (
        <DialogTrigger asChild>
          <Button
            unstyled
            focusStyled
            className="bg-dark-control-base/15 rounded-md flex items-center text-xs font-medium px-2.5 py-1 text-dark-control-base cursor-default hover:bg-dark-control-base/30"
          >
            Suggest an edit
          </Button>
        </DialogTrigger>
      ) : (
        <ButtonLink
          href={getLoginHref(
            `/login?next=${encodeNextParam(pathname + '?contribute')}`
          )}
          unstyled
          focusStyled
          className="bg-dark-control-base/15 rounded-md flex items-center text-xs font-medium px-2.5 py-1 text-dark-control-base cursor-default hover:bg-dark-control-base/30"
        >
          Suggest an edit
        </ButtonLink>
      )}

      <DialogContent
        ref={wrapperRef}
        className="overflow-x-hidden no-scrollbar overflow-y-hidden !p-5"
      >
        <ContributeIntroduction
          onContinue={() => {
            setActiveStepIndex((prevState) => prevState + 1)
          }}
        />
        <ContributeCheckout
          style={{ ...initialStylesForInactiveSteps }}
          onContinue={async (e) => {
            if (
              !userId ||
              !selectedBranchOrCommitQuery.data ||
              isCommit(selectedBranchOrCommitQuery.data)
            ) {
              return
            }

            const formData = new FormData(e.currentTarget)
            const branch = selectedBranchOrCommitQuery.data
            const branchName = z.string().parse(formData.get('branch-name'))
            if (!branchName) {
              toast.error('Branch name is required.')
            }

            await createBranch({
              checkout: true,
              toastSuccess: true,
              toastError: true,
              data: {
                authorId: userId,
                name: branchName,
                createdAt: new Date().toISOString(),
                id: getId(),
                isDefault: false,
              },
              baseBranchOrCommitId: branch.id,
              idSeed: getId(),
            })
          }}
        />
      </DialogContent>
    </Dialog>
  )
}

const ContributeCheckout = ({
  style,
  className,
  onContinue,
}: {
  style?: React.CSSProperties
  className?: string
  onContinue: (e: React.FormEvent<HTMLFormElement>) => void
}) => {
  return (
    <form
      style={style}
      className={className}
      onSubmit={(e) => {
        e.preventDefault()
        onContinue(e)
      }}
    >
      <DialogHeader>
        <DialogTitle>Create a branch to suggest edits</DialogTitle>
        <DialogDescription className="text-sm">
          Freely experiment with the content and request reviews from the repo
          owners.
        </DialogDescription>
      </DialogHeader>

      <LabeledInput
        label="New branch name"
        size="sm"
        name="branch-name"
        className="pb-1.5 mt-3"
        inputProps={{
          placeholder: 'Branch name',
          size: 'sm',
          required: true,
          className: 'w-full',
        }}
      />

      <DialogFooter divider>
        <ButtonLink
          unstyled
          focusStyled
          className="text-xs cursor-pointer hover:underline text-dark-faint mr-auto my-auto max-h-max"
          href="https://docs.basehub.com/documentation/external-contributions"
          target="_blank"
        >
          Learn more about contributions
        </ButtonLink>
        <DialogClose asChild>
          <Button intent="secondary">Cancel</Button>
        </DialogClose>
        <Button type="submit">Create branch</Button>
      </DialogFooter>
    </form>
  )
}

const ContributeIntroduction = ({
  className,
  onContinue,
}: {
  className?: string
  onContinue: () => void
}) => {
  return (
    <div className={className}>
      <DialogHeader>
        <DialogTitle>Guest contributions</DialogTitle>
        <DialogDescription className="text-sm">
          Suggest edits to this repo as a guest by creating your own branch.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-5 mt-6">
        <ContributeStep
          title="Create a new branch from this repo"
          description="In here, you'll be able to make edits to the content."
          icon={<BranchMergeIcon className="size-[14px]" />}
        />
        <ContributeStep
          title="Experiment in your new branch"
          description="Rewrite, upload media, and change the repo's schema without affecting the main branch."
          icon={<Pencil1Icon className="size-[14px] " />}
        />
        <ContributeStep
          title="Send it for review"
          description="Mention members of the repo for them to review and merge your edits."
          icon={<MergeIcon className="size-[14px]" />}
          skipBorder
        />
      </div>

      <DialogFooter divider>
        <DialogClose asChild>
          <Button intent="secondary">Cancel</Button>
        </DialogClose>
        <Button onClick={onContinue} autoFocus>
          Continue
        </Button>
      </DialogFooter>
    </div>
  )
}

const ContributeStep = ({
  icon,
  title,
  description,
  skipBorder,
}: {
  icon: React.ReactNode
  title: string
  description: string
  skipBorder?: boolean
}) => {
  return (
    <div
      className={clsx(
        'flex gap-x-4 relative',
        !skipBorder &&
          'before:content-[""] before:w-px before:h-full before:bottom-0 before:left-3.5 before:absolute before:bg-dark-border-solid before:top-7'
      )}
    >
      <span className="rounded-full border border-dark-border-solid bg-dark-shade size-7 inline-flex items-center justify-center shrink-0">
        {icon}
      </span>
      <div className="space-y-0.5">
        <p className="text-sm">{title}</p>
        <p className="text-xs text-dark-muted">{description}</p>
      </div>
    </div>
  )
}

type LastVisitedBranchReturn<T> = [T] extends [string]
  ? readonly [string, (value: string) => void]
  : readonly [
      Record<string, string>,
      (valueOrUpdater: Record<string, string>) => Promise<void>,
    ]

export const useLastVisitedBranch = <T extends string | undefined>(
  repoSlug?: T
): LastVisitedBranchReturn<T> => {
  const { teamSlug } = useDashboardSlugs()

  const [_, setLastVisitedBranches, __, lastVisitedBranches] = useStorage(
    'sessionStorage',
    (z) => z.record(z.string(), z.string()),
    `last-visited-branch-${teamSlug}`,
    { initialValue: {}, enabled: !!teamSlug }
  )

  if (typeof repoSlug !== 'string') {
    return [
      lastVisitedBranches,
      setLastVisitedBranches,
    ] as unknown as LastVisitedBranchReturn<T>
  }

  const branchSetter = (value: string) => {
    void setLastVisitedBranches({
      ...lastVisitedBranches,
      [repoSlug]: value,
    })
  }

  return [
    lastVisitedBranches[repoSlug] ?? '',
    branchSetter,
  ] as unknown as LastVisitedBranchReturn<T>
}
